import type {
  DropObservation,
  DropOutcome,
  DropRequest,
  ReservationOwner,
  ReservationState,
  ReservationStatus,
} from "@/types/api";
import { formatBytes } from "@/utils/format";

/** One reservation as the reservations table shows it. */
export interface ReservationRow {
  id: string;
  slot: string;
  /** The change capture service that reads the slot. */
  cdcService: string;
  /** The owner as shown: `job@incarnation`, or the job alone. */
  owner: string;
  /** The owner exactly as the engine names it, for a drop's precondition. */
  expectedOwner: ReservationOwner;
  state: ReservationState;
  slotLabel: string;
  lag?: string;
  retained?: string;
  /** The slot's confirmed position, only when it is exact in the browser. */
  confirmedFlushLsn?: number;
  canDrop: boolean;
  needsReconciliation: boolean;
  reason?: string;
}

const SLOT_LABELS: Record<ReservationStatus["slot"]["status"], string> = {
  present: "Present",
  absent: "Absent",
  unknown: "Unknown",
  dropped: "Dropped",
};

function displayOwner(owner: ReservationOwner): string {
  return owner.incarnation ? `${owner.job}@${owner.incarnation}` : owner.job;
}

function bytes(value: number | null): string | undefined {
  return value === null ? undefined : formatBytes(value);
}

function isExactPosition(value: number | null): value is number {
  return value !== null && Number.isSafeInteger(value) && value >= 0;
}

export function reservationRow(status: ReservationStatus): ReservationRow {
  const { reservation, slot } = status;
  const present = slot.status === "present" ? slot : undefined;
  const confirmed = present?.confirmed_flush_lsn ?? null;
  const reason =
    slot.status === "unknown"
      ? slot.reason
      : (reservation.drop_reason ?? undefined);
  return {
    id: reservation.id,
    slot: reservation.identity.slot,
    cdcService: reservation.cdc_service,
    owner: displayOwner(reservation.owner),
    expectedOwner: reservation.owner,
    state: reservation.state,
    slotLabel: SLOT_LABELS[slot.status],
    lag: present && bytes(present.lag_bytes),
    retained: present && bytes(present.retained_wal_bytes),
    confirmedFlushLsn: isExactPosition(confirmed) ? confirmed : undefined,
    canDrop: reservation.state === "retained",
    needsReconciliation: reservation.state === "needs_reconciliation",
    ...(reason ? { reason } : {}),
  };
}

/**
 * The body of a drop. `observed` is what the operator saw of a slot whose
 * earlier drop needs reconciling; a position that can't be sent exactly is
 * refused rather than sent as another one.
 */
export function dropRequest(
  row: ReservationRow,
  opId: string,
  observed?: DropObservation,
): DropRequest {
  if (observed && observed !== "absent") {
    const lsn = observed.slot.confirmed_flush_lsn;
    if (!Number.isSafeInteger(lsn) || lsn < 0) {
      throw new Error(
        `Position ${lsn} is too large to represent exactly in the browser`,
      );
    }
  }
  return {
    reservation: row.id,
    expected_owner: row.expectedOwner,
    op_id: opId,
    ...(observed ? { observed } : {}),
  };
}

export function dropMessage(outcome: DropOutcome): string {
  switch (outcome.outcome) {
    case "dropped":
      return "The slot was dropped, and its retained WAL released.";
    case "needs_reconciliation":
      return `The drop's outcome is unknown, so nothing was sent again: ${outcome.reason}. Check the slot on the server, then reconcile with what you see.`;
    case "in_progress":
      return "Another request is dropping this slot now. Nothing was changed; check again shortly.";
  }
}
